import Bottombar from "@/components/bottombar"
import Button from "@/components/button"
import Topbar from "@/components/topbar"
import Combobox, { ComboboxOption } from "@/components/ui/combobox"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useEnsureBoiloutOfflineBundle } from "@/hooks/use-ensure-boilout-offline-bundle"
import { Icon } from "@/icons/icon"
import { useDisableBack } from "@/lib/utils"
import LogoutConfirmSheet from "@/components/logout-confirm-sheet"
import {
  discardShiftNew,
  getBoiloutOfflineBundleErrorMessage,
  startOfflineBoiloutSession,
  startOrResumeOnlineBoiloutSession,
} from "@/services/boilout"
import useStore from "@/store"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router"
import { HTTPError } from "ky"
import { toast } from "sonner"
import { handleNetworkError, handleOfflineActionError } from "@/services/base"

export default function SelectProductsPage() {
  const navigate = useNavigate()
  const products = useStore.use.boilout().shift.products
  const schema = useStore.use.boilout().schemas.self
  const boiloutStore = useStore.use.boilout()
  const sessionStore = useStore.use.session()
  const { plant } = useStore((state) => state.user)
  const options = useProductOptions()
  const [selected, setSelected] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [showLogoutSheet, setShowLogoutSheet] = useState(false)
  const { bundle, error: bundleError, ensureBundle } =
    useEnsureBoiloutOfflineBundle({
      enabled: !!(boiloutStore.shift.processId || boiloutStore.shift.shiftId),
    })

  useDisableBack()
  useInit()

  function useInit() {
    useEffect(() => {
      // Check if we have shift context
      if (!boiloutStore.shift.processId && !boiloutStore.shift.shiftId) {
        void navigate("/onboard-shift")
        return
      }

      void fetchProductOptions()

      // If we already have products in schema and not viewing completed process, go to shift page
      if (schema.products.length && !boiloutStore.isReadOnly) {
        void navigate("/boilout/form")
      }
    }, [
      bundle,
      boiloutStore.isReadOnly,
      boiloutStore.shift.processId,
      boiloutStore.shift.shiftId,
      navigate,
      schema.products.length,
    ])
  }

  async function fetchProductOptions() {
    if (!boiloutStore.shift.products.length) {
      try {
        const resolvedBundle = bundle || (await ensureBundle())
        if (!resolvedBundle) {
          return
        }

        boiloutStore.setShiftDetails({
          products: resolvedBundle.products.map((p) => ({
            id: p.id,
            name: p.name,
          })),
        })
      } catch (err) {
        console.error("Failed to load BOILOUT offline bundle:", err)
        toast.error("Failed to load products. Please try again.")
      }
    }
  }

  async function handleStartLog() {
    if (!selected.length) {
      toast.error("Please select at least one product")
      return
    }

    setIsLoading(true)

    try {
      if (typeof navigator !== "undefined" && navigator.onLine) {
        const shiftStartTime = sessionStore.shift.startedAt || undefined
        const shiftEndTime = sessionStore.shift.endedAt || undefined
        await startOrResumeOnlineBoiloutSession(selected, shiftStartTime, shiftEndTime)
      } else {
        await startOfflineBoiloutSession(selected)
      }

      sessionStore.startProcess(boiloutStore.shift.processId)
      await navigate("/boilout/form")
    } catch (err) {
      console.error(err)
      toast.error(getBoiloutOfflineBundleErrorMessage(err))
    } finally {
      setIsLoading(false)
    }
  }

  function useProductOptions() {
    const [options, setOptions] = useState<ComboboxOption[]>([])

    useEffect(() => {
      if (!products) {
        return
      }

      setOptions(
        products.map((item) => ({
          label: item.name,
          value: item.id,
        }))
      )
    }, [products])

    return options
  }

  function handleSelectProducts(value: string[]) {
    setSelected(value)
  }

  function isValid() {
    return !!selected.length
  }

  async function handleClickDiscard() {
    try {
      await discardShiftNew()
      await navigate("/manage-processes")
    } catch (err) {
      if (
        handleOfflineActionError(err, { action: "discard", target: "process" })
      ) {
        return
      }

      const handled = await handleNetworkError(err, navigate)

      if (!handled && err instanceof HTTPError) {
        toast.error(err.message)
        return
      }

      toast.error("Error discarding process")
    }
  }

  return (
    <div className="relative h-dvh">
      <Topbar className="sticky" title="BOILOUT Product" subtitle={plant.name}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">
              <Icon name="MoreVertical" className="text-button-black h-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent inPortal>
            <DropdownMenuItem onClick={handleClickDiscard}>
              Discard Process
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setShowLogoutSheet(true)}>
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </Topbar>

      <div className="p-4 pt-8">
        <h2 className="text-button-black text-2xl font-semibold">
          BOILOUT Product
        </h2>

        {bundleError && !options.length && (
          <p className="mt-4 text-sm text-error">{bundleError}</p>
        )}

        <Combobox
          className="mt-6"
          label="Select Products (Multi select)"
          placeholder="Select Products"
          options={options}
          maxAllowed={6}
          multiple
          onChange={handleSelectProducts}
        />
      </div>

      <Bottombar className="absolute bottom-0 flex gap-4">
        <Button
          className="grow"
          disabled={!isValid()}
          isLoading={isLoading}
          onClick={handleStartLog}
        >
          Start Log
        </Button>
      </Bottombar>

      <LogoutConfirmSheet
        open={showLogoutSheet}
        onOpenChange={setShowLogoutSheet}
      />
    </div>
  )
}
